/**
 * Session-scoped holder for the unwrapped O_sk.
 *
 * The accountant types their passphrase once at login → we derive the
 * 32-byte secret + unwrap O_sk → store the raw bytes in this in-memory
 * module. The pull loop reads it; closing the tab forgets it.
 *
 * O_sk is also mirrored into sessionStorage so a soft-reload (F5)
 * doesn't force the user to re-enter their passphrase. sessionStorage
 * dies when the tab closes, which is the correct lifetime — same trust
 * boundary as memory. This is L1 — a stage-2 hardening swap to
 * `crypto.subtle.importKey({ extractable: false })` lands when WebCrypto
 * X25519 ships everywhere we ship.
 */

import { base64 } from "@dovach/shared-crypto";

const STORAGE_KEY = "dovach.osk";

let _osk: Uint8Array | null = null;

export function setOsk(bytes: Uint8Array): void {
  _osk = bytes;
  sessionStorage.setItem(STORAGE_KEY, base64.encode(bytes));
}

export function getOsk(): Uint8Array | null {
  if (_osk) return _osk;
  const raw = sessionStorage.getItem(STORAGE_KEY);
  if (!raw) return null;
  try {
    _osk = base64.decode(raw);
    return _osk;
  } catch {
    return null;
  }
}

export function clearOsk(): void {
  _osk = null;
  sessionStorage.removeItem(STORAGE_KEY);
}

export function isUnlocked(): boolean {
  return getOsk() !== null;
}
